/**
 * ExpandableText — compact display for long text with Show more / Show less toggle.
 * Uses 3-line clamp by default; toggle appears when content likely overflows.
 */

import React, { useState, useRef, useEffect } from 'react';

export interface ExpandableTextProps {
  text: string | null | undefined;
  collapsedLines?: number;
  className?: string;
  buttonClassName?: string;
}

/** Approximate chars per line for overflow heuristic */
const CHARS_PER_LINE = 45;

export const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  collapsedLines = 3,
  className = '',
  buttonClassName = 'text-blue-600 hover:text-blue-800 text-xs font-medium mt-0.5',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const str = text != null ? String(text).trim() : '';
  const isEmpty = str === '';

  useEffect(() => {
    if (!contentRef.current || isExpanded || isEmpty) {
      setHasOverflow(false);
      return;
    }
    const el = contentRef.current;
    const check = () => setHasOverflow(el.scrollHeight > el.clientHeight);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [str, isExpanded, isEmpty]);

  if (isEmpty) return <span className="text-gray-400">-</span>;

  const lineClampStyle: React.CSSProperties = isExpanded
    ? {}
    : {
        display: '-webkit-box',
        WebkitLineClamp: collapsedLines,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
      };

  const showToggle = hasOverflow || isExpanded || str.length > CHARS_PER_LINE * collapsedLines;

  return (
    <div className={className}>
      <div ref={contentRef} style={lineClampStyle} className="whitespace-pre-wrap break-words">
        {str}
      </div>
      {showToggle && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
          }}
          className={buttonClassName}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};
